import {
	PaymentSessionData,
	PAYMENT_DATA_KEY,
	useAdjustLogger,
} from "@/utils/adjust"
import { axios } from "@/utils/axios-wrapper"
import { assetUrl } from "@/utils/cdn"
import { useClientAuth } from "@/utils/client-auth"
import { redirectToLogin } from "@/utils/login-redirect"
import { NotificationContext } from "@/utils/notification"
import { APP_HOST } from "@/utils/variables"
import { useShortCut } from "@kartyom/shared-components"
import clsx from "clsx"
import {
	OfferingInfoResponse,
	OfferingPrices,
	PaymentResponse,
	PaymentStripeUrlRequest,
} from "models/general"
import { useTranslation } from "next-i18next"
import { useRouter } from "next/router"
import { useContext, useEffect, useState } from "react"
import { useQuery } from "react-query"
import { SliderComponent } from "sections/pricing/render-block"
import { v4 } from "uuid"
import Button from "./button"
import { CustomThemedResource } from "./image"
import Loader from "./loader"
import { ErrorMessage } from "./message"
import { SquareSwitch } from "./toggle"

export const TOKENS_TO_VIDEOS_DIVISOR = 2.5

export function fetchOfferingInfo() {
	return async function (): Promise<OfferingInfoResponse> {
		const result = await axios
			.get<OfferingInfoResponse>(
				"/api/v1/membership/offering-info",
			)
			.then((res) => res.data)

		return result
	}
}

export function fetchOfferingPrices() {
	return async function (): Promise<OfferingPrices> {
		const result = await axios
			.get<OfferingPrices>("/api/v1/offering/prices")
			.then((res) => res.data)

		return result
	}
}

export const getCurrentPlan = (
	userEntitlements: string[],
	plans: OfferingInfoResponse["result"],
) => {
	const matchingWeights = userEntitlements
		.filter((entitlement) =>
			plans.available_plans.some(
				(plan) => plan.id === entitlement,
			),
		)
		.map(
			(entitlement) =>
				plans.available_plans.find(
					(plan) => plan.id === entitlement,
				)!.weight,
		)

	if (matchingWeights.length === 0) {
		return { id: "free", title: "Free", weight: -1 }
	}

	const maxWeight = Math.max(...matchingWeights)
	const truePlan = plans.available_plans.find(
		(plan) => plan.weight === maxWeight,
	)!

	return truePlan
}

export const getCurrentPackId = (
	userEntitlements: string[],
	prices: OfferingPrices,
) => {
	let packId = "free"
	let isYearly = false

	userEntitlements.forEach((a) => {
		const canFindMonthly =
			prices.plans.advanced.variants?.find(
				(b) => b.monthly_entitlement === a,
			)

		if (canFindMonthly) {
			packId = canFindMonthly.pack_id
			return
		}

		const canFindYearly =
			prices.plans.advanced.variants?.find(
				(b) => b.yearly_entitlement === a,
			)

		if (canFindYearly) {
			packId = canFindYearly.pack_id
			isYearly = true
			return
		}
	})

	return { packId, isYearly }
}

export const getCurrentPlanText = (
	userEntitlements: string[],
	packId: string,
	isYearly: boolean,
	prices: OfferingPrices,
) => {
	const currentPack = prices.plans.advanced.variants?.find(
		(a) => a.pack_id === packId,
	)
	const tireId = isYearly
		? currentPack?.yearly_entitlement
		: currentPack?.monthly_entitlement
	const isCurrent = userEntitlements.some(
		(a) => a === tireId,
	)
	if (isCurrent) return "txt_current_plan"

	const yearlyTireNames =
		prices.plans.advanced.variants?.map(
			(a) => a.yearly_entitlement,
		)
	const monthlyTireName =
		prices.plans.advanced.variants?.map(
			(a) => a.monthly_entitlement,
		)

	let shouldUpgrade = true

	userEntitlements.forEach((a) => {
		const canFindMonthly = monthlyTireName?.find(
			(b) => a === b,
		)

		if (canFindMonthly && isYearly) {
			shouldUpgrade = true
			return
		}

		if (canFindMonthly && !isYearly) {
			const userPackTokens =
				prices.plans.advanced.variants?.find(
					(c) => c.monthly_entitlement === a,
				)?.monthly_tokens
			const currentPackTokens =
				prices.plans.advanced.variants?.find(
					(c) => c.pack_id === packId,
				)?.monthly_tokens

			if (userPackTokens && currentPackTokens) {
				if (userPackTokens > currentPackTokens) {
					shouldUpgrade = false
					return
				}
			}
		}

		const canFindYearly = yearlyTireNames?.find(
			(b) => a === b,
		)

		if (canFindYearly && !isYearly) {
			shouldUpgrade = false
			return
		}

		if (canFindYearly && isYearly) {
			const userPackTokens =
				prices.plans.advanced.variants?.find(
					(c) => c.yearly_entitlement === a,
				)?.yearly_tokens
			const currentPackTokens =
				prices.plans.advanced.variants?.find(
					(c) => c.pack_id === packId,
				)?.yearly_tokens

			if (userPackTokens && currentPackTokens) {
				if (userPackTokens > currentPackTokens) {
					shouldUpgrade = false
					return
				}
			}
		}
	})

	if (shouldUpgrade) return "txt_upgrade"
	else return "txt_downgrade"
}

export interface SubscriptionPopupProps {
	isOpen: boolean
	close: () => void
	location?: string
	addToStorage?: () => Promise<void>
}
export function SubscriptionPopup(
	props: SubscriptionPopupProps,
) {
	const { isOpen, close, location, addToStorage } = props

	const { userInfo } = useClientAuth()

	const prices = useQuery({
		queryKey: ["offering-prices"],
		queryFn: fetchOfferingPrices(),
		staleTime: Infinity,
		cacheTime: Infinity,
		enabled: isOpen,
	})

	const { packId: currentPackId, isYearly: isPackYearly } =
		userInfo && prices.data
			? getCurrentPackId(userInfo.entitlements, prices.data)
			: { packId: "free", isYearly: false }

	const [isYearly, setIsYearly] = useState(isPackYearly)

	const currentValue =
		prices.data?.plans.advanced.variants?.findIndex(
			(a) => a.pack_id === currentPackId,
		) ?? -1

	const [value, setValue] = useState<number>(
		currentValue !== -1 ? currentValue : 0,
	)

	const [packId, setPackId] = useState(
		prices.data?.plans.advanced.variants?.[value].pack_id ??
			"",
	)

	const currentPlanText =
		userInfo && prices.data
			? getCurrentPlanText(
					userInfo.entitlements,
					packId,
					isYearly,
					prices.data,
			  )
			: "txt_upgrade"

	const { t } = useTranslation("common")
	const { notify } = useContext(NotificationContext)

	const router = useRouter()

	async function handlePurchaseClick() {
		if (userInfo) {
			fetchPaymentLink()
		} else {
			redirectToLogin()
		}
	}

	async function fetchPaymentLink() {
		try {
			const sessionId = v4()
			const path = window.location.pathname
			const search = window.location.search

			const parsedUrl =
				location === undefined ? path + search : location
			const period = isYearly ? "yearly" : "monthly"

			const successUrl = new URL(APP_HOST + parsedUrl)
			successUrl.searchParams.delete(period)
			successUrl.searchParams.delete("session")

			successUrl.searchParams.append(period, "success")
			successUrl.searchParams.append("session", sessionId)

			const cancelUrl = new URL(APP_HOST + parsedUrl)
			cancelUrl.searchParams.delete(period)
			cancelUrl.searchParams.append(period, "cancel")

			const payload: PaymentStripeUrlRequest = {
				success_url: successUrl.toString(),
				cancel_url: cancelUrl.toString(),
				period,
				adjust_id: await getAdId(),
				offering: packId,
			}

			const { url, currency, amount_total } = await axios
				.post<PaymentResponse>(
					"/api/v1/payment/request-stripe-url",
					payload,
				)
				.then((res) => res.data.result)

			logAdjust?.logEvent("initiate_checkout")

			addToStorage && (await addToStorage())

			const paymentData: PaymentSessionData = {
				currency,
				value: amount_total,
				session: sessionId,
			}

			localStorage.setItem(
				PAYMENT_DATA_KEY,
				JSON.stringify(paymentData),
			)

			router.push(url)
		} catch (error) {
			console.error(error)
			notify(
				<ErrorMessage>
					{t("txt_could_not_purchase_now")}
				</ErrorMessage>,
			)
		}
		window.blur()
	}

	const logAdjust = useAdjustLogger()
	useEffect(() => {
		if (!logAdjust) return
		if (!isOpen) return

		logAdjust.logEvent("pricing_page_view")
	}, [isOpen, logAdjust])

	const yearlyPrice =
		prices.data?.plans.advanced.variants?.[value]
			.yearly_price || 0
	const yearlyPriceDivided = yearlyPrice / 12
	const yearlyPriceFormatted = Math.floor(
		yearlyPriceDivided,
	)

	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = "hidden"
		} else {
			document.body.style.overflow = "unset"
		}
	}, [isOpen])

	useShortCut({
		shortCut: "Escape",
		handler: () => {
			close()
		},
	})

	return (
		<div
			onClick={(event) => {
				if (event.target === event.currentTarget) {
					close()
				}
			}}
			className={clsx(
				isOpen ? "block" : "hidden",
				"fixed left-0 top-0 h-full w-full",
				"flex items-center justify-center",
				"z-[999] bg-color-popup",
			)}>
			<div
				className={clsx(
					"relative h-[100svh] w-full overflow-y-scroll bg-color-background tablet:h-auto tablet:w-[658px] tablet:overflow-visible tablet:py-4 desktop:w-[703px]",
					"flex flex-col items-center gap-4 tablet:rounded-[10px]",
				)}>
				<button
					className={clsx(
						"absolute -right-12 top-0",
						"h-[34px] w-[34px] rounded-full",
						"hidden items-center justify-center tablet:flex",
						"bg-color-cell hover:bg-blue-100",
						"transition-colors",
					)}
					onClick={close}>
					<img
						src={assetUrl("/general/close.svg")}
						alt="close popup"
					/>
				</button>
				<div
					className={clsx(
						"relative flex w-full flex-col items-center",
					)}>
					<div className="mt-4 flex w-full flex-row justify-between px-4 tablet:mt-0 tablet:justify-center">
						<p className="text-[20px] font-700 text-blue-900 tablet:text-[24px]">
							{t("txt_pricing_title")}
						</p>
						<button
							className={clsx(
								"h-[34px] w-[34px] rounded-full",
								"flex items-center justify-center",
								"bg-color-cell hover:bg-blue-100",
								"transition-colors tablet:hidden",
							)}
							onClick={close}>
							<img
								src={assetUrl("/general/close.svg")}
								alt="close popup"
							/>
						</button>
					</div>
					<div
						className={clsx(
							"mt-2 flex w-full justify-start gap-[10px] px-4 tablet:justify-center tablet:px-0",
							"transition-opacity",
							prices.isFetching &&
								"pointer-events-none opacity-0",
						)}>
						<span
							className={clsx(
								isYearly
									? "text-blue-500"
									: "text-blue-900",
								"text-[18px] font-500 transition-colors",
							)}>
							{t("txt_monthly")}
						</span>
						<SquareSwitch
							onChange={setIsYearly}
							enabled={isYearly}
							activeColor="var(--color-green-600)"
						/>
						<span
							className={clsx(
								isYearly
									? "text-blue-900"
									: "text-blue-500",
								"text-[18px] font-500 transition-colors",
							)}>
							{t("txt_yearly")}
						</span>
						<span
							className={clsx(
								"text-[18px] font-500 transition-colors",
								isYearly
									? "text-blue-900"
									: "text-blue-500",
							)}>
							{t("fs_save_percent", {
								percent: String(
									prices.data?.save_percent || 0,
								),
							})}
						</span>
					</div>

					<div
						className={clsx(
							"mb-4 mt-4 flex w-full flex-col items-center justify-center gap-6 px-4 tablet:mb-0 tablet:px-5 desktop:flex-row desktop:gap-[27px]",
							"transition-opacity",
							prices.isFetching &&
								"pointer-events-none opacity-0",
						)}>
						{/*PRIME */}

						<div
							className={clsx(
								"flex w-full flex-col overflow-hidden rounded-[8px] border border-green-400",
								"tablet:flex-row desktop:h-[468px] desktop:w-[310px] desktop:flex-col",
								"[box-shadow:0px_5px_12px_0px_rgba(183,183,183,0.2)] dark:[box-shadow:unset]",
							)}>
							<div className="flex w-full flex-1 flex-col justify-between bg-color-surface-cell p-[18px]">
								<div className="flex flex-col">
									<p className="text-[24px] font-600 text-blue-900">
										{t("txt_pro_title")}
									</p>
									<p className="font-300 text-[14px] text-blue-600">
										{t("txt_pro_subtitle")}
									</p>
									<div className="relative mt-3 h-[50px] w-full overflow-hidden">
										<div
											className={clsx(
												"absolute inset-0",
												"flex h-[50px] w-full flex-row items-end justify-start gap-[2px]",
												isYearly && "-top-16",
												"transition-all duration-500",
											)}>
											<span
												className={clsx(
													"font-700 text-blue-900",
													"text-[38px]",
												)}>
												$
												{String(
													prices.data?.plans.advanced
														.variants?.[value]
														.monthly_price,
												).slice(0, -2)}
												.
												<small>
													{String(
														prices.data?.plans.advanced
															.variants?.[value]
															.monthly_price,
													).slice(-2)}
												</small>
											</span>
											<span className="font-300 mb-[10px] text-[14px] lowercase text-blue-600">
												/{t("txt_month")}
											</span>
										</div>

										<div
											className={clsx(
												"absolute inset-0",
												"flex h-[50px] w-full flex-row items-end justify-start gap-[2px]",
												!isYearly && "top-16",
												"transition-all duration-500",
											)}>
											<span
												className={clsx(
													"font-700 text-blue-900",
													"text-[38px]",
												)}>
												$
												{String(yearlyPriceFormatted).slice(
													0,
													-2,
												).length !== 0
													? String(
															yearlyPriceFormatted,
													  ).slice(0, -2)
													: 0}
												.
												<small>
													{String(
														yearlyPriceFormatted,
													).slice(-2)}
												</small>
											</span>
											<span className="font-300 mb-[10px] text-[14px] lowercase text-blue-600">
												/{t("txt_month")}*
											</span>
										</div>
									</div>
								</div>

								{prices.data?.plans.advanced.variants && (
									<SliderComponent
										variants={
											prices.data.plans.advanced.variants
										}
										isYearly={isYearly}
										setPackId={setPackId}
										value={value}
										setValue={setValue}
									/>
								)}

								<div className="flex w-full">
									<Button
										secondary
										disabled={
											currentPlanText === "txt_current_plan"
										}
										onClick={handlePurchaseClick}
										className="w-full !font-500 disabled:!bg-green-100 disabled:!text-color-inactive-text disabled:hover:!bg-green-100"
										text={currentPlanText}></Button>
								</div>
							</div>
							<div className="h-[1px] w-full bg-blue-100 tablet:h-full tablet:w-[1px] desktop:h-[1px] desktop:w-full" />
							<div className="flex flex-col gap-2 bg-blue-50 p-4 tablet:w-[310px] tablet:gap-4 desktop:h-[160px] desktop:w-full desktop:gap-1">
								<div className="flex items-center gap-[10px] ">
									<CustomThemedResource
										format="svg"
										source="/pricing/check"
									/>
									<span className="text-[14px] font-400 text-blue-800">
										{isYearly
											? t("fs_tokens_to_videos_year", {
													tokens:
														prices.data?.plans.advanced
															.variants?.[value]
															.yearly_tokens,
													videos: Math.ceil(
														(prices.data?.plans.advanced
															.variants?.[value]
															.yearly_tokens ?? 0) /
															TOKENS_TO_VIDEOS_DIVISOR,
													),
											  })
											: t("fs_tokens_to_videos_month", {
													tokens:
														prices.data?.plans.advanced
															.variants?.[value]
															.monthly_tokens,
													videos: Math.ceil(
														(prices.data?.plans.advanced
															.variants?.[value]
															.monthly_tokens ?? 0) /
															TOKENS_TO_VIDEOS_DIVISOR,
													),
											  })}
									</span>
								</div>

								{prices.data?.plans.advanced.points.map(
									(point) => {
										return (
											<div
												className="flex items-center gap-[10px] "
												key={point}>
												<CustomThemedResource
													format="svg"
													source="/pricing/check"
												/>
												<span className="text-[14px] font-400 text-blue-800">
													{point}
												</span>
											</div>
										)
									},
								)}
							</div>
						</div>

						{/* Enterprise */}

						<div
							className={clsx(
								"flex w-full flex-col overflow-hidden rounded-[8px]",
								"tablet:flex-row desktop:h-[468px] desktop:w-[310px] desktop:flex-col",
								"[box-shadow:0px_5px_12px_0px_rgba(183,183,183,0.2)] dark:[box-shadow:unset]",
							)}>
							<div className="flex w-full flex-1 flex-col justify-between bg-color-surface-cell p-[18px]">
								<div className="flex flex-col">
									<p className="text-[24px] font-600 text-blue-900">
										{t("txt_enterprise_title")}
									</p>
									<p className="font-300 text-[14px] text-blue-600">
										{t("txt_enterprise_subtitle")}
									</p>
									<p className="mt-3 text-[38px] font-800 text-blue-800">
										{t("txt_custom")}
									</p>
								</div>
								<div className="mt-8 flex w-full tablet:mt-12 desktop:mt-0">
									<Button
										outlineSecondary
										href={
											prices.data?.plans.enterprise
												.contact_url
										}
										onClick={() =>
											logAdjust?.logEventWithPayload(
												"w_dp_cs",
												{ from: router.asPath },
											)
										}
										className="w-full"
										text="txt_contact_sales"></Button>
								</div>
							</div>
							<div className="h-[1px] w-full bg-blue-100 tablet:h-full tablet:w-[1px] desktop:h-[1px] desktop:w-full" />
							<div className="flex flex-col gap-2 bg-blue-50 p-4 tablet:w-[310px] tablet:gap-4 desktop:h-[160px] desktop:w-full desktop:gap-1">
								{prices.data?.plans.enterprise.points.map(
									(point) => {
										return (
											<div
												className="flex items-center gap-[10px] "
												key={point}>
												<CustomThemedResource
													format="svg"
													source="/pricing/check"
												/>
												<span className="text-[14px] font-400 text-blue-800">
													{point}
												</span>
											</div>
										)
									},
								)}
							</div>
						</div>
					</div>

					{prices.isFetching && (
						<div className="absolute left-0 top-0 flex h-full w-full items-center justify-center">
							<Loader />
						</div>
					)}
				</div>
				<p
					className={clsx(
						" text-[16px] font-400 text-blue-500 transition-opacity",
						isYearly ? "opacity-100" : "opacity-0",
					)}>
					*{t("txt_billed_yearly")}
				</p>
			</div>
		</div>
	)
}

export async function getAdId(): Promise<string | null> {
	// This was implemented when using adjust
	return null
}
